import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Shield, Eye, Database, Lock, Trash2, Mail } from 'lucide-react';

function FadeSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >{children}</motion.div>
  );
}

export default function PrivacyPolicy() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const sections = [
    { icon: <Eye size={22} />, title: '1. Information We Collect', content: 'We collect personal information you voluntarily provide when using our contact form, demo submission form, or newsletter signup. This includes your name, email address, and any message content you submit. We also automatically collect certain technical data such as IP address, browser type, and pages visited through standard web analytics.' },
    { icon: <Database size={22} />, title: '2. How We Use Your Information', content: 'Your information is used to: respond to your inquiries and messages; review demo submissions; send label updates if you have opted in; improve our website and services; comply with legal obligations. We do not sell, rent, or share your personal data with third parties for marketing purposes.' },
    { icon: <Lock size={22} />, title: '3. Data Security', content: 'We implement industry-standard security measures to protect your personal information, including SSL encryption for all data transmission, secure server infrastructure, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.' },
    { icon: <Shield size={22} />, title: '4. Your Rights', content: 'You have the right to: access, correct, or delete your personal data; withdraw consent at any time; request a copy of your data; lodge a complaint with a supervisory authority. To exercise these rights, contact us at info@cloudfimusic.com.' },
    { icon: <Trash2 size={22} />, title: '5. Data Retention', content: 'We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy. Contact form submissions are retained for up to 24 months. You may request deletion at any time by emailing info@cloudfimusic.com.' },
    { icon: <Mail size={22} />, title: '6. Contact', content: 'For any questions about this Privacy Policy, please contact: CloudfiMusic / Xu\u00e2n Di\u1ec7u Media Co., Ltd. Email: info@cloudfimusic.com. Company ID: 0402222404.' },
  ];

  return (
    <div className="pp-page">
      <motion.div className="pp-hero" ref={heroRef}>
        <motion.div className="pp-hero-inner" style={{ y: heroY }}>
          <motion.div className="pp-hero-icon"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: 'spring', stiffness: 200 }}
          ><Shield size={48} /></motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>Privacy Policy</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>Last updated: March 2025</motion.p>
        </motion.div>
      </motion.div>

      <div className="pp-content">
        {sections.map((s, i) => (
          <FadeSection key={i} delay={i * 0.08}>
            <div className="pp-card">
              <div className="pp-card-icon">{s.icon}</div>
              <h2>{s.title}</h2>
              <p>{s.content}</p>
            </div>
          </FadeSection>
        ))}
      </div>

      <div className="pp-footer-nav">
        <Link to="/terms-of-service">Terms of Service &rarr;</Link>
        <Link to="/">Back to Home</Link>
      </div>
    </div>
  );
}
